import { memo, useCallback,  useMemo, useState } from "react"

import Text from "../../components/text/Text";
import Button from "../../components/button/Button";
import Input from "../../components/input/Input";
import Toast from "../../components/toast/Toast";

import useStore from "@/src/hooks/useStore";

import "./LoginPopup.scss";
import useKeydown from "@/src/hooks/useKeydown";
import XTREAM from "@/src/services/api/xtream";

import PlaylistLoading from "@/src/pages/playlist/playlistLoading/PlaylistLoading";

import ExitPopup from "../exitPopup/ExitPopup";

import logo from "@/src/branding/dream-4k/logo.png"
import Config from "@/src/branding/dream-4k/Config";

import PlaylistStorage from "@/src/services/playlists";
import { GetPlaylistContent } from "@/src/services/content/content";
import { isValidHost } from "@/src/utils/util";

function LoginPopup({ onBack }) {

    const [activeIndex, setActiveIndex] = useState(0)
    const [focusedInput, setFocusedInput] = useState(null)
    const [loading, setLoading] = useState(false);
    const [isCanceled, setIsCanceled] = useState(false);
    const [exitPopup, setExitPopup] = useState(false);
    const [platform] = useStore("platform");

    const [inputs, setInputs] = useState([
        { label: "host", value: "", helpKeys: ["http://", "https://", ".com", ".ru", ":80", ":8080"] },
        { label: "username", value: "", helpKeys: [] },
        { label: "password", value: "", helpKeys: [] }
    ])

    const onBlur = (value) => {

        setFocusedInput(null);

        inputs[activeIndex].value = value.trim();

        setInputs([...inputs])

    }

    const login = useCallback(async () => {

        // a trailing slash would break the urls built from host (host + "/player_api.php")
        const host = inputs[0].value.replace(/\/+$/, "");
        const username = inputs[1].value;
        const password = inputs[2].value;

        if (!host) return Toast.error({ msg: "host is required", id: "login_form" });
        if (!isValidHost(host)) return Toast.error({ msg: "incorrect host", id: "login_form" });
        if (!username) return Toast.error({ msg: "username is required", id: "login_form" });
        if (!password) return Toast.error({ msg: "password is required", id: "login_form" });

        setLoading(true);

        try {
            const data = await XTREAM.login({ host, username, password });

            await confirm({ host, username, password, data });
        } catch (e) {
            console.log(e);

            // errorLogout = the server didn't answer: wrong host, or a panel that rejects bad credentials with an http error
            Toast.error({ msg: e == "errorLogout" ? "incorrect host, username or password" : e, id: "login_form" });
        }

        setLoading(false);

    }, [])

    const confirm = useCallback(async (xtream_account) => {

        const { host, username, password,data } = xtream_account;

        const url = `${host}/get.php?username=${username}&password=${password}&type=m3u_plus&output=ts`

        try {

            await XTREAM.init({ host, username, password },data);

            // saved on this device only; on failure the form keeps what was typed
            const result = await PlaylistStorage.add({ name: username, url });

            if (result.success === false) return error(result.message);

            // logs in, selects the playlist and loads its content
            const res = await GetPlaylistContent(result.playlist);

            if (res == 'errorLogout') {
                // an account that doesn't load isn't kept (no-op if loading already removed it)
                await PlaylistStorage.remove(result.playlist.id);
                return error("incorrect host, username or password");
            }

            onBack();

        } catch (e) {
            console.log(e);
            error(e);
        }

    }, [])

    const error = useCallback((msg) => {

        Toast.error({ msg, id: "login_form" });

        setLoading(false);

    }, [])

    const cancel = useCallback(() => {

        setLoading(false);

        XTREAM.abortAll();

        setIsCanceled(true);

    }, [])

    const onExit = useCallback(() => {

        platform.exit();

    }, [platform])

    const handleCheckInputType = ({  index }) => {

        setFocusedInput(index)
    }

    const handleMouseEnter = (index) => {

        setActiveIndex(index)
    }

    const keydown = useMemo(() => {

        return {
            isActive: !exitPopup && focusedInput == null,

            keydown: (e, key) => {
                const keys = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

                if (activeIndex < inputs.length && keys.indexOf(key) > -1) {
                    inputs[activeIndex].value += key;
                    setInputs([...inputs]);

                    return true
                }
            },

            down: () => {
                if (activeIndex === inputs.length) return;
                setActiveIndex(activeIndex + 1)
            },

            up: () => {
                if (activeIndex === 0) return;
                setActiveIndex(activeIndex - 1)
            },

            back: () => {
                if (loading) return;

                if (window.Android) {
                    onExit();
                } else {
                    setExitPopup(true);
                }
            }
        }

    }, [activeIndex, exitPopup, focusedInput, inputs])

    useKeydown(keydown);

    return (

        <>
            {<div className="login_popup" style={{ backgroundImage: `url(${Config.loginBackground})` }}>
                <div className="login_popup_content">

                    <div className="logo">
                        <img src={logo} />
                    </div>

                    <div className="title"><Text>Login</Text></div>

                    <div className="inputs">

                        {inputs.map((input, index) => {
                            return (
                                <Button
                                    className={`button`}
                                    key={index}
                                    data={index}
                                    isActive={activeIndex === index && !exitPopup}
                                    onMouseEnter={() => handleMouseEnter(index)}
                                    onClick={() => handleCheckInputType({ ...input, index })}
                                >

                                    <Input
                                        key={index}
                                        placeholder={input.label}
                                        initialValue={input.value}
                                        isFocused={focusedInput === index}
                                        onBlur={onBlur}
                                        onDone={onBlur}
                                        onTop={onBlur}
                                        onBack={onBlur}
                                        helpKeys={input.helpKeys}
                                    />
                                </Button>
                            )
                        })}

                    </div>

                    <div className="buttons_parent">
                        <Button
                            className={"button"}
                            isActive={activeIndex === inputs.length && !exitPopup}
                            data={inputs.length}
                            onMouseEnter={() => handleMouseEnter(inputs.length)}
                            onClick={login}
                        >
                            <Text>Login</Text>
                        </Button>
                    </div>
                </div>

                {loading && <PlaylistLoading status="loading" onBack={cancel} />}
            </div>}

            {exitPopup && <ExitPopup isVisible={exitPopup} onExit={onExit} onCancel={() => setExitPopup(false)} />}

        </>

    )
}

export default memo(LoginPopup);